import { useCallback, useEffect, useState } from "react";
import { Copy, Save } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { copyText } from "@/lib/utils";
import { Card } from "@/components/ui/Card";
import { FormMessage, type Feedback } from "@/components/ui/FormMessage";
import { DEFAULT_QRMAINT_CONFIG, type GenericResponse, type QrMaintConfig } from "@/lib/types";

function parseIds(raw: string): number[] {
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => parseInt(s, 10))
    .filter((n) => Number.isFinite(n));
}

function joinIds(ids: number[]): string {
  return ids.join(", ");
}

const EVENT_TYPES = [
  { code: "WORK_STATUS_CHANGED", level: "INFO / WARN / ERROR *", desc: t("Auftragsstatus hat sich geändert") },
  { code: "NEW_WORK_ORDER", level: "INFO", desc: t("Neuer Arbeitsauftrag erstellt") },
  { code: "NEW_REPORT", level: "INFO", desc: t("Neuer Bericht erstellt") },
];

export function QrMaintWebhookPage() {
  const [cfg, setCfg] = useState<QrMaintConfig>(DEFAULT_QRMAINT_CONFIG);
  const [warnIds, setWarnIds] = useState("");
  const [errorIds, setErrorIds] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const webhookUrl = `${window.location.origin}/api/webhook/qrmaint`;

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<QrMaintConfig>("/api/settings/qrmaint-webhook");
      setCfg(data);
      setWarnIds(joinIds(data.warn_status_ids ?? []));
      setErrorIds(joinIds(data.error_status_ids ?? []));
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Laden fehlgeschlagen") });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    const body: QrMaintConfig = {
      ...cfg,
      warn_status_ids: parseIds(warnIds),
      error_status_ids: parseIds(errorIds),
    };
    try {
      const resp = await api.post<GenericResponse>("/api/settings/qrmaint-webhook", body);
      if (resp.success) {
        setFeedback({ kind: "success", text: resp.message });
        await reload();
      } else {
        setFeedback({ kind: "error", text: resp.message });
      }
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Speichern fehlgeschlagen") });
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = () => {
    void copyText(webhookUrl).then((ok) => {
      if (ok) setFeedback({ kind: "success", text: t("Webhook-URL kopiert") });
    });
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <FormMessage feedback={feedback} />

      <Card title={t("Webhook-URL")} description={t("Trage diese URL in der QR.Maint-Webhook-Konfiguration ein.")}>
        <div className="flex items-center justify-between gap-2 rounded-md bg-muted/60 px-3 py-2 font-mono text-xs">
          <span className="truncate">{webhookUrl}</span>
          <button
            type="button"
            onClick={copyUrl}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-xs hover:bg-muted"
          >
            <Copy className="h-3 w-3" /> {t("Kopieren")}
          </button>
        </div>
      </Card>

      <Card title={t("Konfiguration")}>
        {loading ? (
          <div className="text-sm text-muted-foreground">{t("Lade…")}</div>
        ) : (
          <form onSubmit={onSave} className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="flex cursor-pointer items-center gap-2 md:col-span-2">
              <input
                type="checkbox"
                checked={cfg.enabled}
                onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                className="h-4 w-4 cursor-pointer accent-primary"
              />
              <span className="text-sm">{t("Webhook aktiv")}</span>
            </label>

            <Field
              label={t("Client-ID")}
              hint={t("Einträge werden unter dieser ID gespeichert")}
              value={cfg.client_id}
              onChange={(v) => setCfg({ ...cfg, client_id: v })}
              placeholder="qrmaint"
            />

            <Field
              label={t("HMAC-Secret")}
              hint={t("leer = keine Signaturprüfung")}
              value={cfg.secret}
              onChange={(v) => setCfg({ ...cfg, secret: v })}
              placeholder={t("Webhook-Secret aus QR.Maint")}
              type="password"
            />

            <Field
              label={t("WARN Status-IDs")}
              hint={t("kommagetrennt, z.B. 690,691")}
              value={warnIds}
              onChange={setWarnIds}
              placeholder="690, 691"
              mono
            />

            <Field
              label={t("ERROR Status-IDs")}
              hint={t("kommagetrennt, z.B. 700,701")}
              value={errorIds}
              onChange={setErrorIds}
              placeholder="700, 701"
              mono
            />

            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground disabled:opacity-60"
              >
                <Save className="h-3.5 w-3.5" /> {saving ? t("Speichern…") : t("Einstellungen speichern")}
              </button>
            </div>
          </form>
        )}
      </Card>

      <Card title={t("Unterstützte Events")}>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              <th className="py-2 text-left font-medium">EventTypeId</th>
              <th className="py-2 text-left font-medium">{t("Standard-Level")}</th>
              <th className="py-2 text-left font-medium">{t("Beschreibung")}</th>
            </tr>
          </thead>
          <tbody>
            {EVENT_TYPES.map((ev) => (
              <tr key={ev.code} className="border-b border-border last:border-b-0">
                <td className="py-2 pr-3 font-mono">{ev.code}</td>
                <td className="py-2 pr-3 text-muted-foreground">{ev.level}</td>
                <td className="py-2">{ev.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-[11px] text-muted-foreground">
          {t("* Level wird durch die WARN/ERROR-Status-IDs oben gesteuert.")}
        </p>
      </Card>
    </div>
  );
}

function Field({
  label,
  hint,
  value,
  onChange,
  placeholder,
  type = "text",
  mono = false,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: "text" | "password";
  mono?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        className={
          "h-9 rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary " +
          (mono ? "font-mono text-xs" : "")
        }
      />
      {hint ? <span className="text-[11px] text-muted-foreground">{hint}</span> : null}
    </label>
  );
}
